import * as React from "react";
import { Tabs as ReactUITabs } from "react-ui";

import { PushInstance } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { usePrevious } from "../../../Helpers/ReactUtils/Hooks/UsePrevious";
import { AbsoluteModelPath, AbsoluteResolvedModelPath, PathTokens } from "../../../../../Common/ModelPath/ModelPath";
import { ProvideInstanceIndex } from "../../Internals/ControlContexts/PathContexts/InstanceIndexContext";
import { noop } from "../../../../../Common/TypingUtils";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { ofReactType } from "../../TypingUtils";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";

import * as styles from "./Tabs.styles";
import { TabsTestPageObject } from "./TabsTestPageObject";

export interface TabsProps {
    bindingPath: AbsoluteResolvedModelPath;
    instances: readonly string[];
    currentTab?: string;
    children: React.ReactNode[];
    onAddInstance: () => void;
    onRemoveInstance: (bindingPath: AbsoluteResolvedModelPath, instance: string) => void;
    revalidateChildrenPaths: (bindingPath: AbsoluteResolvedModelPath) => void;
    calculateCurrentTabIndex: (path: AbsoluteModelPath, lastToken: string) => string;
    setCurrentTabIndex: (instance: string | undefined) => void;
    setCurrentTab: (instance: string | undefined) => void;
}

export function Tabs(props: TabsProps): JSX.Element {
    const prevInstances = usePrevious(props.instances);

    React.useEffect(() => {
        props.revalidateChildrenPaths(props.bindingPath);

        return () => {
            onChangeIndex(undefined);
        };
    }, []);

    React.useEffect(() => {
        if (props.instances.length === 0) {
            onChangeIndex(undefined);
        }
        if (prevInstances != undefined && prevInstances.length !== props.instances.length) {
            if (props.instances.length === prevInstances.length + 1) {
                onChangeIndex(props.instances[props.instances.length - 1]);
                return undefined;
            }
        }
        if (props.currentTab != undefined && !props.instances.includes(props.currentTab)) {
            onChangeIndex(props.instances[0]);
            return undefined;
        }

        if (props.currentTab == undefined && (prevInstances?.length ?? 0) === 0 && props.instances.length > 0) {
            onChangeIndex(props.instances[0]);
        }

        if (props.instances.length > 0) {
            onChangeIndex(props.instances[0]);
        }

        return undefined;
    }, [props.instances.join()]);

    const handleRequestPathAvailability = async (path: AbsoluteModelPath) => {
        const lastToken = IterUtils.last(path.getPathParts());
        if (PathTokens.isInstanceToken(lastToken)) {
            const tabIndex = props.calculateCurrentTabIndex(path.getParentPath(), lastToken);
            onChangeIndex(tabIndex);
        }
    };

    const handleSetPage = (instance: string | undefined) => {
        if (instance != undefined && props.instances.includes(instance)) {
            onChangeIndex(instance ?? "0");
        }
    };

    const handleRemoveInstance = (instance: string) => {
        props.onRemoveInstance(props.bindingPath, instance);
        if (PathTokens.isInstanceToken(instance)) {
            const tabIndex = props.calculateCurrentTabIndex(props.bindingPath, instance);
            props.setCurrentTabIndex(tabIndex);
        }
    };

    const onChangeIndex = (instance: string | undefined) => {
        if (instance == undefined) {
            props.setCurrentTab(undefined);
            props.setCurrentTabIndex(undefined);
            return;
        }
        props.setCurrentTab(instance);
        if (PathTokens.isInstanceToken(instance)) {
            const tabIndex = props.calculateCurrentTabIndex(props.bindingPath, instance);
            props.setCurrentTabIndex(tabIndex);
        }
    };

    useTestPageObject(
        () => TabsTestPageObject(props.instances, props.onAddInstance, handleRemoveInstance, handleSetPage),
        [props.instances]
    );

    return (
        <FocusManagementObserver onRequestFocus={noop} onRequestPathAvailability={handleRequestPathAvailability}>
            <div data-tid="Root">
                <div data-tid="Items">
                    <ReactUITabs value={props.currentTab ?? ""} onValueChange={onChangeIndex}>
                        {props.instances.map((instance, index) => (
                            <ReactUITabs.Tab id={instance} key={instance}>
                                <ProvideInstanceIndex index={index} bindingPath={props.bindingPath}>
                                    <PushInstance instance={instance} bindingPath={props.bindingPath}>
                                        <>{props.children.find(x => x !== ofReactType(TabContent))}</>
                                    </PushInstance>
                                </ProvideInstanceIndex>
                            </ReactUITabs.Tab>
                        ))}
                    </ReactUITabs>
                </div>
                <div data-tid="CurrentContent" key={props.currentTab} css={styles.items}>
                    {props.currentTab && (
                        <PushInstance instance={props.currentTab} bindingPath={props.bindingPath}>
                            <>{props.children.find(ofReactType(TabContent))}</>
                        </PushInstance>
                    )}
                </div>
            </div>
        </FocusManagementObserver>
    );
}

interface TabContentProps {
    children: React.ReactNode;
}

export function TabContent(props: TabContentProps): JSX.Element {
    return <>{props.children}</>;
}
